import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_RMIS_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

const formSchema = z.object({
  clientID: z.string().min(1, 'Client ID is required'),
  domainName: z.string().optional(),
  rmisPassword: z.string().min(1, 'Client Password is required')
})

/**
 * This is the modal for the RMIS app.
 */
export function RMISModal({ isOpen, onClose, appType }: AppModalProps) {
  const [formError, setFormError] = useState('')

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      clientID: '',
      domainName: '',
      rmisPassword: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [enableRMISApp, { loading }] = useMutation(ENABLE_RMIS_APP, {
    onCompleted: () => {
      toast.success('RMIS app is enabled successfully!')
      handleClose()
    },
    onError: (err) => {
      const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableRMISApp({
      variables: {
        appType: appType as AppTypeEnum,
        clientID: values.clientID,
        domainName: values.domainName || undefined,
        password: values.rmisPassword
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable RMIS for Chain</DialogTitle>
          <DialogDescription>
            RMIS integration requires an API Client ID and Client Password. This can be requested from your RMIS rep.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="clientID"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>RMIS API Client ID</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your RMIS Client ID" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rmisPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>RMIS API Client Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Enter your RMIS Client Password"
                      {...field}
                      disabled={loading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="domainName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>RMIS Domain Name (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="YourDomainName.rmissecure.com" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href="https://handbook.chaineapp.com/product-guide/apps/rmis"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                See detailed steps here
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                  {loading ? 'Enabling...' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
